// Reconoce un vehículo en lo escrito, esté antes o después de la pieza:
// "2006 npr-hd shock absorber", "shock absorber isuzu npr hd 2006 4hk1 5.2".
// Al elegirlo, la búsqueda filtra por sus aplicaciones (fitment del índice =
// ids de application_options_vehicles) y sigue con el resto del texto.
//
// Los vehículos, cada uno con su familia y la imagen del camión, salen de la
// colección <tienda>_vehicles de Typesense (loadVehicles en
// utils/productSearch.js; la llena search-sync). Se descargan enteros una vez
// y aquí se buscan en memoria, sin llamadas mientras se escribe.
//
// - Modelo: obligatorio. "npr-hd", "npr hd" y "nprhd" son lo mismo. Si hay
//   varios, el de más palabras ("npr hd" gana a "npr"). Varias marcas pueden
//   tener el mismo modelo (Isuzu y General Motors NPR-HD).
// - Marca ("isuzu"), año (4 cifras), motor ("4hk1") y cilindrada ("5.2",
//   "5.2l"): opcionales; si están y encajan, afinan la selección y salen del
//   texto.
// - Un modelo que sólo son cifras ("268") sólo cuenta junto a su marca, para
//   no confundirlo con un código.
//
// Lo que se elige es siempre UN vehículo (marca, modelo, año, motor): las
// filas de la tabla que sólo cambian en algo que no se ve (región: United
// States, Canada, Non-USA...) se juntan en una opción con todos sus ids.

const squash = text =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const MAX_MODEL_WORDS = 3;
// "General Motors" son dos palabras.
const MAX_MAKE_WORDS = 3;
const YEAR = /^(19|20)\d{2}$/;
// "5.2", "5,2", "5.2l"
const LITER = /^(\d{1,2})[.,](\d)l?$/i;

/** Índice a partir de los documentos de la colección de vehículos. */
export const buildVehicleIndex = (vehicles = []) => {
  const models = new Map();
  vehicles.forEach(vehicle => {
    const key = squash(vehicle.model);
    if (key.length < 2) return;
    if (!models.has(key)) {
      models.set(key, {
        model: vehicle.model,
        digitsOnly: /^\d+$/.test(key),
        versions: []
      });
    }
    models.get(key).versions.push({
      id: String(vehicle.id),
      model: vehicle.model,
      make: vehicle.make || "",
      makeKey: squash(vehicle.make),
      year: Number(vehicle.year),
      engine: vehicle.engine_name || "",
      engineKey: squash(vehicle.engine_name),
      liter:
        vehicle.engine_liter === null || vehicle.engine_liter === undefined
          ? null
          : Number(vehicle.engine_liter),
      region: vehicle.region || "",
      family: vehicle.catalog_code
        ? {
            catalogCode: vehicle.catalog_code,
            family: vehicle.family || vehicle.catalog_code,
            imageUrl: vehicle.image_url || null
          }
        : null
    });
  });
  return { models };
};

/** "4HK1 5.2L" */
export const engineLabel = option =>
  [
    option.engine,
    option.liter !== null && option.liter !== undefined
      ? `${Number(option.liter).toFixed(1)}L`
      : ""
  ]
    .filter(Boolean)
    .join(" ");

// Versiones iguales a la vista (marca, año, motor, cilindrada, familia) en una
// sola opción con todos sus ids. De la más nueva a la más vieja.
const groupOptions = versions => {
  const groups = new Map();
  versions.forEach(version => {
    const key = [
      version.makeKey,
      squash(version.model),
      version.year,
      version.engineKey,
      version.liter,
      version.family ? version.family.catalogCode : ""
    ].join("|");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        ids: [],
        regions: [],
        make: version.make,
        model: version.model,
        year: version.year,
        engine: version.engine,
        liter: version.liter,
        family: version.family
      });
    }
    const group = groups.get(key);
    group.ids.push(version.id);
    if (version.region && !group.regions.includes(version.region)) {
      group.regions.push(version.region);
    }
  });
  return [...groups.values()].sort(
    (a, b) =>
      b.year - a.year ||
      a.make.localeCompare(b.make) ||
      a.model.localeCompare(b.model) ||
      engineLabel(a).localeCompare(engineLabel(b))
  );
};

// Familias de las opciones, de la más a la menos repetida.
const familiesOf = options => {
  const counts = new Map();
  options.forEach(option => {
    if (!option.family) return;
    const key = option.family.catalogCode;
    const entry = counts.get(key) || { family: option.family, count: 0 };
    entry.count++;
    counts.set(key, entry);
  });
  return [...counts.values()]
    .sort((a, b) => b.count - a.count)
    .map(entry => entry.family);
};

// La marca escrita (de una o varias palabras, fuera de las del modelo) entre
// las que tiene ese modelo: { key, indices } o null.
const findMake = (keys, model, modelStart, modelSize) => {
  const makeKeys = new Set(model.versions.map(v => v.makeKey));
  const outside = i => i < modelStart || i >= modelStart + modelSize;
  for (let size = MAX_MAKE_WORDS; size >= 1; size--) {
    for (let start = 0; start + size <= keys.length; start++) {
      const indices = Array.from({ length: size }, (_, n) => start + n);
      if (!indices.every(outside)) continue;
      const key = keys.slice(start, start + size).join("");
      if (makeKeys.has(key)) return { key, indices };
    }
  }
  return null;
};

/**
 * El vehículo de lo escrito, o null:
 * { model, make, makes, year, options, years, image, label, detail, indices }.
 * `options` son los vehículos concretos que encajan (ver groupOptions): si hay
 * uno, está elegido; si no, la página pide el año y el motor. `indices` son
 * las palabras de la búsqueda que lo describen (se quitan del texto al
 * elegirlo).
 */
export const detectVehicle = (query, index) => {
  if (!index || !query) return null;
  const words = String(query)
    .split(/\s+/)
    .filter(Boolean);
  const keys = words.map(squash);

  // Modelo: la frase más larga que coincide, empezando por la izquierda.
  let found = null;
  for (let size = Math.min(MAX_MODEL_WORDS, words.length); size >= 1; size--) {
    for (let start = 0; start + size <= words.length && !found; start++) {
      const model = index.models.get(keys.slice(start, start + size).join(""));
      if (!model) continue;
      const make = findMake(keys, model, start, size);
      if (model.digitsOnly && !make) continue;
      found = { model, start, size, make };
    }
    if (found) break;
  }
  if (!found) return null;

  const { model, start, size, make: typedMake } = found;
  const used = new Set();
  for (let i = start; i < start + size; i++) used.add(i);

  let versions = model.versions;

  // Marca escrita: sólo esa.
  if (typedMake) {
    versions = versions.filter(v => v.makeKey === typedMake.key);
    typedMake.indices.forEach(i => used.add(i));
  }

  // Cada afinado sólo se aplica si deja alguna versión.
  const narrow = (at, keep) => {
    if (at === -1) return false;
    const kept = versions.filter(keep);
    if (!kept.length) return false;
    versions = kept;
    used.add(at);
    return true;
  };

  // Año
  const yearAt = keys.findIndex(
    (key, i) =>
      !used.has(i) &&
      YEAR.test(key) &&
      versions.some(v => v.year === Number(key))
  );
  const year = narrow(yearAt, v => v.year === Number(keys[yearAt]))
    ? Number(keys[yearAt])
    : null;

  // Cilindrada: "5.2" (antes de squash, que quita el punto)
  const literAt = words.findIndex(
    (word, i) => !used.has(i) && LITER.test(word)
  );
  if (literAt !== -1) {
    const [, whole, decimal] = LITER.exec(words[literAt]);
    const liter = Number(`${whole}.${decimal}`);
    narrow(literAt, v => v.liter !== null && Math.abs(v.liter - liter) < 0.05);
  }

  // Motor: una palabra con letras y cifras por la que empieza el motor de
  // alguna versión ("4hk1" -> "4HK1-TCN EPA07").
  const engineAt = keys.findIndex(
    (key, i) =>
      !used.has(i) &&
      key.length >= 3 &&
      /\d/.test(key) &&
      /[a-z]/.test(key) &&
      versions.some(v => v.engineKey.startsWith(key))
  );
  narrow(engineAt, v => v.engineKey.startsWith(keys[engineAt]));

  return summarize(versions, {
    year,
    indices: [...used].sort((a, b) => a - b)
  });
};

// Lo que devuelven detectVehicle y vehicleFromVin a partir de las versiones
// que encajan: las opciones concretas y cómo presentarlas.
const summarize = (versions, { year = null, indices = [] } = {}) => {
  const options = groupOptions(versions);
  const makes = [...new Set(options.map(o => o.make))];
  const models = [...new Set(options.map(o => o.model))];
  const make = makes.length === 1 ? makes[0] : null;
  const years = [...new Set(options.map(o => o.year))];

  // El nombre de la familia sólo si es una ("npr" sin año son muchas); la
  // imagen, de la más repetida que tenga foto.
  const families = familiesOf(options);
  const withImage = families.find(f => f.imageUrl);
  const model = models.join(" / ");
  const base = [make, model].filter(Boolean).join(" ");
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const detail = [
    makes.length > 1 ? makes.join(" / ") : null,
    families.length === 1
      ? families[0].family
      : families.length > 1
      ? `${families.length} families`
      : null,
    year ? null : minYear === maxYear ? minYear : `${minYear}–${maxYear}`,
    `${options.length} vehicle${options.length === 1 ? "" : "s"}`
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    model,
    make,
    makes,
    year,
    options,
    years,
    base,
    image: withImage ? withImage.imageUrl : null,
    label: [base, year].filter(Boolean).join(" "),
    detail,
    indices
  };
};

/**
 * Nuestros vehículos que corresponden a un VIN decodificado (utils/vinDecode.js),
 * con la misma forma que detectVehicle, o null si ninguno encaja. `wordIndex`
 * es la posición del VIN en lo escrito (sale del texto al elegirlo).
 *
 * La NHTSA y nuestra tabla no nombran igual, así que no se compara el texto
 * tal cual, sino por pasos:
 *   modelo  obligatorio. "NQR/NRR" -> NQR o NRR; sin guiones ni espacios.
 *   año     obligatorio y exacto: el VIN lo codifica sin ambigüedad.
 *   marca   si coincide; si no ("CHEVROLET" vs "General Motors"), no descarta.
 *   motor   "4HK1-TC" encaja con "4HK1" (en las dos direcciones).
 *   litros  5.2 vs 5.2, con margen de 0.05.
 * Marca, motor y litros sólo afinan si dejan alguna versión.
 */
export const vehicleFromVin = (decoded, index, wordIndex) => {
  if (!index || !decoded || !decoded.ok) return null;
  const modelKeys = [
    squash(decoded.model),
    ...String(decoded.model)
      .split(/[/,]/)
      .map(squash)
  ].filter(key => key.length >= 2);
  let versions = [...new Set(modelKeys)].flatMap(key =>
    index.models.has(key) ? index.models.get(key).versions : []
  );
  versions = versions.filter(v => v.year === decoded.year);
  if (!versions.length) return null;

  const narrow = keep => {
    const kept = versions.filter(keep);
    if (kept.length) versions = kept;
  };
  const makeKey = squash(decoded.make);
  narrow(v => v.makeKey === makeKey);
  const engineKey = squash(decoded.engine);
  if (engineKey) {
    narrow(
      v =>
        v.engineKey &&
        (engineKey.startsWith(v.engineKey) || v.engineKey.startsWith(engineKey))
    );
  }
  if (decoded.liters) {
    narrow(v => v.liter !== null && Math.abs(v.liter - decoded.liters) < 0.05);
  }
  return summarize(versions, { year: decoded.year, indices: [wordIndex] });
};

/**
 * Un vehículo concreto (una opción de `match.options`), listo para filtrar
 * por todos sus ids. Lleva las posiciones de las palabras de `match` para
 * quitarlas del texto.
 */
export const specificVehicle = (match, option) => ({
  indices: match.indices,
  ids: option.ids,
  label: `${option.make} ${option.model} ${option.year} · ${engineLabel(
    option
  )}`,
  detail: option.family ? option.family.family : "",
  image:
    option.family && option.family.imageUrl
      ? option.family.imageUrl
      : match.image
});
